import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import Seo from '../ui/Seo';
import Reveal from '../ui/Reveal';
import Hero from '../sections/Hero';
import TradeTicker from '../sections/TradeTicker';
import Statement from '../sections/Statement';
import FrameScrubber from '../sections/FrameScrubber';
import StoryDeck from '../sections/StoryDeck';
import SectionHead from '../sections/SectionHead';
import ProcessRail from '../sections/ProcessRail';
import Bento from '../sections/Bento';
import Pledge from '../sections/Pledge';
import Reviews from '../sections/Reviews';
import ContactSection from '../sections/ContactSection';
import { ProjectGrid, ProjectModal } from '../sections/ProjectCards';
import { projects } from '../data/projects.js';
import { contact, site } from '../data/site';

import backgroundVideo from '../media/videos/backgroundVideo.mp4';
import heroPoster from '../media/images/home/lutron-keypad.jpg';

import ResidentialImg from '../media/images/residential-services/electrical-panel-upgrades.jpg';
import CommercialImg from '../media/images/commercial-services/installations-upgrades.jpg';
import MaintenanceImg from '../media/images/electrical-maintenance/preventative-maintenance.jpg';
import PowerImg from '../media/images/power-systems/backup-generators.jpg';
import LightingImg from '../media/images/lighting-solutions/commercial-lighting.jpg';
import EfficiencyImg from '../media/images/energy-efficiency/led-lighting-upgrades.jpg';

const disciplines = [
    {
        id: 'residential',
        kicker: 'Homes',
        title: 'Residential Services',
        text: 'Panels, service upgrades, recessed lighting, generators and landscape wiring, sized for the loads a modern house actually puts on a system that was often specified decades ago. Same-day service calls across the tri-county area.',
        imgSrc: ResidentialImg,
        alt: 'Residential electrical panel upgrade',
        focus: '20%',
        points: ['Panel upgrades', 'Service upgrades', 'LED recessed', 'Generators', 'EV charging']
    },
    {
        id: 'commercial',
        kicker: 'Business',
        title: 'Commercial Services',
        text: 'Fit-outs, retrofits and new construction for buildings that cannot afford a dark day. We work to opening dates, coordinate around the other trades, and pass inspection the first time rather than the third.',
        imgSrc: CommercialImg,
        alt: 'Commercial electrical installation',
        focus: '50%',
        points: ['New construction', 'Warehouse lighting', 'Code corrections', 'Surge protection']
    },
    {
        id: 'maintenance',
        kicker: 'Uptime',
        title: 'Electrical Maintenance',
        text: 'Scheduled inspection, thermal diagnostics and emergency response. Faults found on a planned visit cost a fraction of the same fault found on the worst possible day, which is the entire argument for a maintenance contract.',
        imgSrc: MaintenanceImg,
        alt: 'Preventative electrical maintenance',
        focus: '50%',
        points: ['Preventative', 'Diagnostics', 'Emergency repairs', 'Safety inspections']
    },
    {
        id: 'power',
        kicker: 'Resilience',
        title: 'Power Systems',
        text: 'Standby generation, UPS, battery storage and solar, specified against your real load profile and commissioned properly rather than left for the next contractor to work out. Documented for whoever maintains it next.',
        imgSrc: PowerImg,
        alt: 'Backup generator installation',
        focus: '50%',
        points: ['Generators', 'UPS', 'Battery storage', 'Solar / PV']
    },
    {
        id: 'lighting',
        kicker: 'Design',
        title: 'Lighting Solutions',
        text: 'Layout, colour temperature, control and code, decided before the first can is cut in. The difference between a lit room and a good one is all specification, and it is almost impossible to fix afterwards.',
        imgSrc: LightingImg,
        alt: 'Commercial lighting design',
        focus: '50%',
        points: ['Architectural', 'Smart control', 'Landscape', 'Emergency & exit']
    },
    {
        id: 'efficiency',
        kicker: 'Payback',
        title: 'Energy Efficiency',
        text: 'We audit what you actually consume before proposing anything, so the payback period on the work is a number rather than a hope. LED retrofits, HVAC controls, demand response and solar integration.',
        imgSrc: EfficiencyImg,
        alt: 'LED lighting upgrade',
        focus: '50%',
        points: ['Energy audits', 'LED retrofit', 'Smart management', 'Demand response']
    }
];

export default function Home() {
    const [selectedProject, setSelectedProject] = useState(null);

    return (
        <>
            <Seo
                title="Electrical Contractor in South Florida"
                description="Smart Electric Solutions is a licensed residential and commercial electrical contractor serving South Florida: panels, power systems, lighting, maintenance and energy efficiency. Call +1 (305) 205-6409."
                keywords="electrical contractor South Florida, electrician Hollywood FL, panel upgrade, generator installation, commercial electrician, LED lighting, energy efficiency, EV charging"
                path="/"
                schema={[
                    {
                        '@type': 'Electrician',
                        '@id': `${site.origin}/#business`,
                        name: site.name,
                        url: `${site.origin}/`,
                        description: site.blurb,
                        telephone: '+1-305-205-6409',
                        address: {
                            '@type': 'PostalAddress',
                            streetAddress: '3040 N 29th Ave #1',
                            addressLocality: 'Hollywood',
                            addressRegion: 'FL',
                            postalCode: '33020',
                            addressCountry: 'US'
                        },
                        areaServed: 'South Florida',
                        parentOrganization: {
                            '@type': 'Organization',
                            name: 'The Smart Solutions Group',
                            url: 'https://thesmartsolutionsgroup.net/'
                        }
                    },
                    { '@type': 'WebPage', name: 'Home', url: `${site.origin}/` }
                ]}
            />

            <Hero
                video={backgroundVideo}
                poster={heroPoster}
                title="Power, engineered end to end."
                lead="A licensed residential and commercial electrical contractor bringing a century-old profession into the 21st century, across South Florida."
                notice={{ tag: 'New', text: 'Now partnered with TPI Ingeniería', href: '/tpi' }}
            />

            <TradeTicker />

            <Statement eyebrow="Who we are" />

            <FrameScrubber
                path="/media/commitment_to_exelence_frames/"
                count={80}
                runway={320}
                focus={0.55}
                eyebrow="Our commitment"
                title="Excellence is a process, not a promise."
                body="From the load calculation to the final trim, the same standard applies at every step. Scroll to watch a system come together."
                captions={[
                    { text: 'Load calculated, system designed', at: [0.06, 0.28] },
                    { text: 'Rough-in run and inspected', at: [0.32, 0.54] },
                    { text: 'Devices trimmed and terminated', at: [0.58, 0.78] },
                    { text: 'Commissioned, documented, handed over', at: [0.82, 0.98] }
                ]}
            />

            <section className="section section--ruled" id="solutions">
                <div className="shell">
                    <SectionHead
                        eyebrow="What we do"
                        title="Six disciplines. One contractor."
                        lead="High voltage through low, service entrance through last cover plate. Every part of the electrical package is ours, so nobody is left arguing about whose scope the gap belonged to."
                    />
                </div>

                <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                    <StoryDeck items={disciplines} />
                </div>
            </section>

            <ProcessRail eyebrow="How it works" title="One call replaces the juggling act." />

            <section className="section section--raised">
                <div className="shell">
                    <SectionHead
                        eyebrow="Track record"
                        title="Landmark work behind us."
                        lead="Our executive leadership has delivered the electrical scope on some of South Florida’s most recognizable buildings, working alongside Coastal, Suffolk, Moss and John Moriarty & Associates."
                    />

                    <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        <ProjectGrid items={projects.slice(0, 4)} onSelect={setSelectedProject} />
                    </div>

                    <Reveal delay={0.1} style={{ textAlign: 'center', marginTop: 'clamp(2.5rem, 5vw, 3.5rem)' }}>
                        <Link className="btn btn--accent" to="/portfolio">
                            See all {projects.length} projects
                            <span className="btn__arrow" aria-hidden="true">→</span>
                        </Link>
                    </Reveal>
                </div>
            </section>

            <Bento
                id="why-us"
                eyebrow="Why us"
                title="A contractor built for complex programmes."
                lead="Licensed, bonded and technology-forward, with leadership that has run billion-dollar electrical volume. Here is what that buys you."
                outro="One streamlined, expertly managed approach to the whole electrical package, backed by the low-voltage and interior construction companies of The Smart Solutions Group."
            />

            <Reviews />

            <section className="section section--ruled">
                <div className="shell">
                    <SectionHead eyebrow="The commitment" title="What you get, every time." center />

                    <div style={{ marginTop: 'clamp(2.5rem, 6vw, 4rem)' }}>
                        <Pledge />
                    </div>

                    <Reveal delay={0.1} className="bento__outro">
                        <p className="t-lead">
                            Serving South Florida from Hollywood: {contact.serviceArea}{' '}
                            <Link className="link" to="/services">See the full service list</Link>.
                        </p>
                    </Reveal>
                </div>
            </section>

            <ContactSection />

            {selectedProject && (
                <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
            )}
        </>
    );
}
